import { useEffect, useRef, useState } from 'react';
import { MessageCircle, Send } from 'lucide-react';
import { gameStore } from '@/store/gameStore';
import { sessionStore } from '@/store/sessionStore';
import { useSendChatMessage } from '@/lib/socket/useGameSocket';
import { Avatar } from '@/design-system/components/Avatar';
import { t } from '@/lib/i18n';
import styles from './ChatPanel.module.css';

interface Props {
  roundId: string;
  /** 'dark' cho user side, 'light' cho admin side */
  theme?: 'dark' | 'light';
}

export function ChatPanel({ roundId, theme = 'dark' }: Props) {
  const chatMessages = gameStore((s) => s.chatMessages);
  const myId = sessionStore((s) => s.user?.id);
  const sendMessage = useSendChatMessage(roundId);

  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll khi có tin nhắn mới
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatMessages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    sendMessage(input.trim());
    setInput('');
    inputRef.current?.focus();
  };

  return (
    <div className={`${styles.root} ${styles[theme]}`}>
      <div className={styles.header}>
        <MessageCircle size={14} />
        <span>{t('chat.title')}</span>
      </div>

      <div className={styles.messageList} ref={listRef}>
        {chatMessages.length === 0 ? (
          <div className={styles.empty}>{t('chat.empty')}</div>
        ) : (
          chatMessages.map((msg) => {
            const isMe = msg.sender_id === myId;
            return (
              <div
                key={msg.id}
                className={`${styles.msgRow} ${isMe ? styles.msgRowMe : ''}`}
              >
                {!isMe && (
                  <div className={styles.avatarWrap}>
                    <Avatar
                      src={msg.sender_avatar ?? undefined}
                      name={msg.sender_name ?? '?'}
                      size="xs"
                    />
                  </div>
                )}
                <div className={`${styles.bubble} ${isMe ? styles.bubbleMe : ''} ${msg.is_host ? styles.bubbleHost : ''}`}>
                  {!isMe && (
                    <span className={styles.senderName}>
                      {msg.sender_name ?? t('chat.unknownSender')}
                      {msg.is_host && <span className={styles.hostBadge}>{t('chat.hostBadge')}</span>}
                    </span>
                  )}
                  <span className={styles.msgText}>{msg.message}</span>
                  <span className={styles.msgTime}>
                    {new Date(msg.sent_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form className={styles.inputRow} onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          className={styles.input}
          placeholder={t('chat.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={200}
          autoComplete="off"
        />
        <button type="submit" className={styles.sendBtn} disabled={!input.trim()}>
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
